import { supabase } from './supabase'
import { toutesLesLignes } from './pages'
import type { Commande } from './types'
import { PREFIXES_PAR_DEFAUT, type PosteOhada } from './ohada'

/**
 * Comptabilité SYSCOHADA — MODÈLE AGENT.
 *
 * ⚠️ La règle qui gouverne tout : le produit de SwiftEats est la commission +
 * la livraison + les produits accessoires + Premium. **Jamais le GMV.**
 * L'argent des articles appartient au marchand et ne fait que transiter : il
 * entre en trésorerie et ressort en dette (401). Le confondre avec du chiffre
 * d'affaires le multiplierait par cinq et enverrait l'entreprise dans le
 * mauvais régime fiscal.
 *
 * Toutes les RÈGLES d'écriture vivent en base (`transaction_types`) : pour
 * chaque événement, quel compte au débit et au crédit. Rien n'est écrit en dur
 * ici — un plan de secours codé en dur reproduirait le défaut corrigé par la
 * migration 0025, où l'écran et la base ne pointaient pas les mêmes comptes.
 */

export interface CompteSyscohada {
  code: string
  label: string
  class: number
  kind: string
}

export interface TypeTransaction {
  code: string
  name: string
  debit: string
  credit: string
  kind: string
  auto: boolean
}

export interface EcritureManuelle {
  id: string
  date: string
  tx: string
  amount: number
  label: string
}

export interface PlanComptable {
  comptes: CompteSyscohada[]
  types: TypeTransaction[]
  manuelles: EcritureManuelle[]
  /** Réglé dans Config. Un taux est un paramètre comptable, pas un barème. */
  tva: ParametresTVA
}

/**
 * Le taux seul, pour les écrans qui ne chargent pas tout le plan comptable.
 *
 * Il en existe un : l'écran Live affiche un « revenu » calculé par la même
 * fonction que la comptabilité. Sans ce chargement, il l'afficherait toutes
 * taxes comprises pendant que la Comptabilité l'affiche hors taxes — deux
 * écrans de la même console en désaccord de 18 %, sans que rien ne l'indique.
 *
 * En cas d'échec de lecture on rend la TVA INACTIVE. C'est le repli sûr : il
 * sous-estime le produit au lieu de le surestimer, et ne fabrique jamais une
 * taxe qui n'a pas été facturée.
 */
export async function chargerParametresTVA(): Promise<ParametresTVA> {
  const { data, error } = await supabase
    .from('platform_config')
    .select('vat_enabled,vat_rate')
    .eq('id', 1)
    .single()
  if (error || !data) return TVA_INACTIVE
  const c = data as unknown as { vat_enabled: boolean | null; vat_rate: number | null }
  return { actif: Boolean(c.vat_enabled), taux: Number(c.vat_rate ?? 0) }
}

/**
 * Charge le plan comptable depuis la base.
 *
 * ⚠️ En cas d'échec, on renvoie VIDE et l'écran le dit. Servir un plan de
 * secours écrit en dur donnerait une comptabilité d'apparence normale, calculée
 * sur des comptes qui ne sont pas ceux de l'entreprise.
 */
export async function chargerPlan(): Promise<PlanComptable> {
  const [comptes, types, manuelles, tva] = await Promise.all([
    supabase.from('syscohada_accounts').select('*').order('code'),
    supabase.from('transaction_types').select('*'),
    // ⚠️ La colonne s'appelle `entry_date`, pas `date` : `date` est un mot
    // réservé SQL. Trier sur un nom inexistant fait échouer toute la requête.
    supabase.from('manual_entries').select('*').order('entry_date', { ascending: false }),
    chargerParametresTVA(),
  ])
  if (comptes.error) throw comptes.error
  if (types.error) throw types.error

  return {
    tva,
    comptes: ((comptes.data ?? []) as Array<{
      code: string
      label: string
      class: number | null
      kind: string | null
    }>).map((a) => ({
      code: String(a.code),
      label: a.label,
      class: a.class ?? 0,
      kind: a.kind ?? 'other',
    })),
    types: ((types.data ?? []) as Array<{
      code: string
      name: string
      debit_account: string
      credit_account: string
      kind: string
      auto_generate: boolean
    }>).map((t) => ({
      code: t.code,
      name: t.name,
      debit: String(t.debit_account),
      credit: String(t.credit_account),
      kind: t.kind,
      auto: Boolean(t.auto_generate),
    })),
    manuelles: ((manuelles.data ?? []) as Array<{
      id: string
      entry_date: string
      tx_type: string
      amount: number
      label: string | null
    }>).map((m) => ({
      id: m.id,
      date: String(m.entry_date).slice(0, 10),
      tx: m.tx_type,
      amount: m.amount,
      label: m.label ?? '',
    })),
  }
}

// ── Édition du plan comptable ────────────────────────────────────────────

/**
 * Créer, modifier et supprimer comptes et types d'écritures.
 *
 * ⚠️ Ces quatre fonctions manquaient entièrement au portage : les deux écrans
 * étaient en LECTURE SEULE, alors que l'original permet de les éditer. Un plan
 * comptable figé oblige à passer par la base à la main dès qu'une charge
 * nouvelle apparaît — une assurance, un loyer — et c'est exactement ce qu'un
 * responsable non-technicien ne peut pas faire.
 *
 * Rien n'est validé ici « pour être gentil » : les contraintes vivent en base
 * (clé primaire, clés étrangères vers `syscohada_accounts`) et leurs refus
 * remontent tels quels. Ce sont ces refus qui protègent la comptabilité —
 * notamment l'impossibilité de supprimer un compte encore référencé.
 */

/* Codes et libellés sont réinjectés partout dans l'écran. Plutôt que
   d'échapper à chaque endroit — et d'en oublier un — on interdit à la source
   les caractères qui permettent de sortir d'une balise. Aucun libellé
   SYSCOHADA légitime n'en contient ; `&` reste permis (« Achats & services »). */
const nettoyerLibelle = (s: string) => String(s ?? '').replace(/[<>"']/g, '').trim()

export async function enregistrerCompte(c: {
  code: string
  label: string
  class: number
  kind: string
}): Promise<void> {
  const code = nettoyerLibelle(c.code)
  if (!code) throw new Error('Le code du compte est obligatoire.')
  const { error } = await supabase.from('syscohada_accounts').upsert({
    code,
    label: nettoyerLibelle(c.label) || code,
    class: c.class,
    kind: nettoyerLibelle(c.kind) || 'other',
  })
  if (error) throw error
}

export async function supprimerCompte(code: string): Promise<void> {
  const { error } = await supabase.from('syscohada_accounts').delete().eq('code', code)
  /* Un compte référencé par un type d'écriture est retenu par une clé
     étrangère. Le message brut de Postgres ne dit pas pourquoi à quelqu'un qui
     n'est pas comptable — on l'explique, sans masquer l'erreur d'origine. */
  if (error)
    throw new Error(
      `${error.message}

Un compte référencé par un type d'écriture ne peut pas être supprimé.`,
    )
}

export async function enregistrerType(t: {
  code: string
  name: string
  debit: string
  credit: string
  kind: string
  auto: boolean
}): Promise<void> {
  const code = nettoyerLibelle(t.code).toUpperCase()
  if (!code) throw new Error("Le code du type d'écriture est obligatoire.")
  if (!t.debit || !t.credit) throw new Error('Comptes de débit et de crédit obligatoires.')
  if (t.debit === t.credit)
    throw new Error('Débit et crédit ne peuvent pas être le même compte : l’écriture serait nulle.')
  const { error } = await supabase.from('transaction_types').upsert({
    code,
    name: nettoyerLibelle(t.name) || code,
    debit_account: t.debit,
    credit_account: t.credit,
    kind: t.kind,
    auto_generate: t.auto,
  })
  if (error) throw error
}

export async function supprimerType(code: string): Promise<void> {
  const { error } = await supabase.from('transaction_types').delete().eq('code', code)
  if (error)
    throw new Error(
      `${error.message}

Un type encore utilisé par des écritures ne peut pas être supprimé.`,
    )
}

// ── Décomposition d'une commande ─────────────────────────────────────────

export interface Decomposition {
  /** ⚠️ TTC — ce que le client paie et ce qu'on retient au marchand. */
  livraison: number
  accessoires: number
  articles: number
  commission: number
  dûAuMarchand: number
  partLivreur: number
  date: string
  ref: string
  /** Ce que le client a payé — le GMV. Jamais du chiffre d'affaires. */
  encaisse: number
  /** Ce qui nous revient réellement, HORS TAXES. Le CA est toujours HT. */
  revenu: number
  /** Ventilation fiscale des trois produits ci-dessus. */
  livraisonHT: number
  accessoiresHT: number
  commissionHT: number
  /** Collectée pour le compte de l'État. Ni produit, ni charge : une dette. */
  tvaCollectee: number
  /** Reversé en entier au livreur. Transit, jamais du chiffre d'affaires. */
  pourboire: number
}

/**
 * Ce que touche le livreur pour une commande — la MÊME règle que le calcul de
 * sa paie (`compute_driver_payouts`, 0019) : `driver_pay` s'il est renseigné,
 * sinon l'ancienne assiette `delivery_fee + weather_fee`.
 *
 * ⚠️ Avant le 03/10/2026, la compta prenait 70 % des frais de livraison à
 * défaut, et ne comptait RIEN quand `driver_pay` valait 0. La dette due au
 * livreur (4013) n'aurait jamais été soldée par sa paie : les deux calculs
 * doivent dire la même chose au franc près.
 */
export function partDuLivreur(o: Pick<Commande, 'partLivreur' | 'fraisLivraison' | 'fraisMeteo'>): number {
  if ((o.partLivreur ?? 0) > 0) return o.partLivreur as number
  return (o.fraisLivraison || 0) + (o.fraisMeteo || 0)
}

/**
 * Paramètres de TVA, tels que réglés dans Config.
 *
 * `actif` à faux rend `{ ht: montant, tva: 0 }` : c'est ce qui garantit qu'une
 * entreprise non assujettie obtient exactement les chiffres d'avant la
 * migration 0039, sans aucune branche particulière ailleurs dans le calcul.
 */
export interface ParametresTVA {
  actif: boolean
  /** FRACTION (0.18), jamais le pourcentage. */
  taux: number
}

export const TVA_INACTIVE: ParametresTVA = { actif: false, taux: 0 }

/**
 * Extrait la taxe d'un montant TOUTES TAXES COMPRISES.
 *
 * Le sens compte : `ht = ttc / (1 + taux)`, et surtout PAS `ttc × (1 − taux)`.
 * À 18 %, la deuxième formule se trompe de 3,2 % — assez peu pour passer
 * inaperçue à l'œil, assez pour fausser une déclaration.
 *
 * L'arrondi se fait sur le HT et la taxe prend le reste : c'est ce qui assure
 * que `ht + tva` redonne le TTC AU FRANC PRÈS. Arrondir les deux séparément
 * laisserait un franc dans la nature à chaque commande, et le contrôle du
 * tableau des flux finirait par le voir.
 */
export function extraireTVA(ttc: number, tva: ParametresTVA): { ht: number; tva: number } {
  if (!tva.actif || tva.taux <= 0 || ttc <= 0) return { ht: ttc, tva: 0 }
  const ht = Math.round(ttc / (1 + tva.taux))
  return { ht, tva: ttc - ht }
}

export function decomposer(o: Commande, tva: ParametresTVA = TVA_INACTIVE): Decomposition {
  /* La majoration « Forte demande » (0054, `tension_fee`) est un supplément du
     PRIX DE LA LIVRAISON : même produit (706) que les frais de livraison. Sa
     part livreur est déjà dans `driver_pay`, donc dans `partLivreur` (4013). */
  const livraison = (o.fraisLivraison || 0) + (o.fraisTension || 0)
  // Produits accessoires (707) : service + petite commande + météo. Les deux
  // derniers étaient absents de la requête et valaient donc toujours zéro.
  const accessoires = (o.fraisService || 0) + (o.fraisPetiteCommande || 0) + (o.fraisMeteo || 0)

  /* Commission au prorata des lignes. `commission_pct` est le taux FIGÉ à la
     commande : c'est la seule source, et elle est immuable — modifier le taux
     d'un article aujourd'hui ne doit rien changer aux commandes passées.
     Une commande sans ligne d'article n'a PAS de CA articles : on ne le déduit
     pas du total, qui contient livraison et frais de service. Lui appliquer un
     taux inventé fabriquerait une commission jamais facturée à personne. */
  const lignes = o.articles ?? []
  const montant = (l: (typeof lignes)[number]) => l.total ?? l.unit_price * l.quantity
  const articles = lignes.reduce((s, l) => s + montant(l), 0)
  /* 0055 — commission AJOUTÉE : la ligne porte son montant figé (prix client −
     prix marchand). Ancien modèle : taux × total, comme avant. Même règle que
     `compute_merchant_payouts`, pour que dette marchand et reversement disent
     la même chose au franc près. */
  const commission = Math.round(
    lignes.reduce((s, l) => s + (l.montant_commission ?? montant(l) * (l.commission_pct ?? 0)), 0),
  )
  /* ⚠️ La retenue au marchand se calcule sur la commission TTC, pas HT. La TVA
     ne change RIEN à ce qu'on lui doit : c'est un partage de recette convenu
     entre nous, la taxe n'est qu'une ventilation interne de notre part. Passer
     ici le montant hors taxes lui reverserait 18 % de commission de trop. */
  const dûAuMarchand = Math.max(0, articles - commission)
  const partLivreur = partDuLivreur(o)

  /* Les trois produits sont taxés séparément, et non sur leur somme : les
     arrondis par ligne sont ceux qui figureront au journal, ligne par ligne.
     Taxer le total puis répartir laisserait le journal en désaccord avec
     lui-même d'un franc ou deux. */
  const tCom = extraireTVA(commission, tva)
  const tLiv = extraireTVA(livraison, tva)
  const tAcc = extraireTVA(accessoires, tva)

  return {
    livraison,
    accessoires,
    articles,
    commission,
    dûAuMarchand,
    partLivreur,
    date: (o.horodatages.delivered ?? o.creeeLe ?? '').slice(0, 10),
    ref: o.ref ?? o.id,
    encaisse: o.total || 0,
    commissionHT: tCom.ht,
    livraisonHT: tLiv.ht,
    accessoiresHT: tAcc.ht,
    tvaCollectee: tCom.tva + tLiv.tva + tAcc.tva,
    revenu: tCom.ht + tLiv.ht + tAcc.ht,
    pourboire: o.pourboire ?? 0,
  }
}

// ── Événements, journal, grand-livre ─────────────────────────────────────

export interface EvenementComptable {
  date: string
  tx: string
  montant: number
  libelle: string
  source: 'order' | 'payment' | 'refund' | 'merchant_payout' | 'merchant_adjustment' | 'driver_payout' | 'driver_bonus' | 'fee' | 'manual' | 'premium'
}

// ── Les mouvements d'argent RÉELS ────────────────────────────────────────

export type Operateur = 'wave' | 'orange_money'

/** Un paiement client reçu (et éventuellement remboursé depuis). */
export interface PaiementRecu {
  id: string
  commandeId: string
  operateur: string
  montant: number
  /** `refunded` = reçu PUIS remboursé : l'encaissement a bien eu lieu. */
  statut: 'succeeded' | 'refunded'
  recuLe: string
  rembourseLe: string | null
}

/**
 * Un paiement d'abonnement Premium reçu (`payments.kind = 'premium'`, migration 0062).
 * Pas de commande : c'est NOTRE produit (7063), encaissé directement — il ne passe pas par
 * le pivot 4191 des commandes.
 */
export interface PaiementPremium {
  id: string
  clientId: string
  operateur: string
  montant: number
  statut: 'succeeded' | 'refunded'
  recuLe: string
}

/** Un lot de paie ou de reversement RÉELLEMENT versé (`status = 'paid'`). */
export interface Versement {
  id: string
  beneficiaire: string
  operateur: string | null
  /** Ce qui est RÉELLEMENT parti en trésorerie (`net_payable`, retenues déjà déduites). */
  montant: number
  verseLe: string
  /**
   * Reversement marchand (0065) : part des frais de virement retenue au marchand (0 par défaut : les frais sont
   * inclus dans notre commission). Elle solde le 401 sans sortie d'argent (401 / 7068) ; l'envoi lui-même coûte
   * le tarif de l'opérateur (631, via `fraisOperateur`).
   */
  fraisRetenus?: number
}

/**
 * Retenue (négatif) ou crédit (positif) décidé après un reversement — remboursement client, litige, correction
 * (`merchant_adjustments`, 0065). Écriture 401 / 4191 pour une retenue, 4191 / 401 pour un crédit : de la dette et de
 * l'avance client, jamais un produit.
 */
export interface AjustementMarchand {
  id: string
  beneficiaire: string
  /** Signé, comme en base. */
  montant: number
  /** Date de Bamako. */
  date: string
  motif: string
}

/**
 * Une prime acquise par un livreur (migration 0060, RPC `primes_livreurs`) :
 * pointe (jour de la course), volume (jour où le seuil est atteint), fidélité
 * (dernier jour du mois). C'est la MÊME source que le calcul de la paie : la
 * charge passée ici (637 / 4013) est exactement ce que le lot versé rembourse.
 */
export interface PrimeAcquise {
  livreurId: string
  genre: 'pointe' | 'volume' | 'fidelite'
  /** `AAAA-MM-JJ`, date de Bamako. */
  jour: string
  montant: number
  courses: number
}

/** Taux des opérateurs, en FRACTION. `null` = pas encore connu. */
export interface TauxMobileMoney {
  encaissement: Record<Operateur, number | null>
  decaissement: Record<Operateur, number | null>
}

export interface Mouvements {
  /** Paiements de COMMANDES seulement. */
  paiements: PaiementRecu[]
  /** Paiements d'abonnement Premium (0062). Vide tant que la migration n'est pas en base. */
  abonnementsPremium: PaiementPremium[]
  paiesLivreurs: Versement[]
  reversementsMarchands: Versement[]
  /** Retenues et crédits marchands (0065). Vide tant que la migration n'est pas en base. */
  ajustementsMarchands?: AjustementMarchand[]
  /** Primes dues aux livreurs (0060). Vide tant que la migration n'est pas en base. */
  primesLivreurs: PrimeAcquise[]
  taux: TauxMobileMoney
}

export const TAUX_INCONNUS: TauxMobileMoney = {
  encaissement: { wave: null, orange_money: null },
  decaissement: { wave: null, orange_money: null },
}

export const MOUVEMENTS_VIDES: Mouvements = {
  paiements: [],
  abonnementsPremium: [],
  paiesLivreurs: [],
  reversementsMarchands: [],
  ajustementsMarchands: [],
  primesLivreurs: [],
  taux: TAUX_INCONNUS,
}

const jour = (horodatage: string | null | undefined) => (horodatage ?? '').slice(0, 10)

const taux = (v: unknown): number | null =>
  v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? null : Number(v)

/**
 * Charge les mouvements d'argent réels : paiements reçus, paies et
 * reversements versés, taux des opérateurs.
 *
 * ⚠️ Une colonne absente (migration 0044 pas encore poussée) laisse les taux
 * à `null` : la comptabilité dit alors qu'ils manquent, elle n'invente rien.
 */
export async function chargerMouvements(): Promise<Mouvements> {
  const [paiements, paies, reversements, cfg, primes, ajustements] = await Promise.all([
    toutesLesLignes<Record<string, unknown>>((de, a) =>
      supabase
        .from('payments')
        .select('*')
        .in('status', ['succeeded', 'refunded'])
        .order('created_at')
        .order('id')
        .range(de, a),
    ),
    toutesLesLignes<Record<string, unknown>>((de, a) =>
      supabase
        .from('driver_payouts')
        .select('id,driver_id,provider,total,paid_at,period_end')
        .eq('status', 'paid')
        .order('period_end')
        .order('id')
        .range(de, a),
    ),
    toutesLesLignes<Record<string, unknown>>((de, a) =>
      supabase
        .from('merchant_payouts')
        // `*` : `frais_marchand` (0065) n'existe pas tant que la migration n'est pas poussée.
        .select('*')
        .eq('status', 'paid')
        .order('period_end')
        .order('id')
        .range(de, a),
    ),
    supabase.from('platform_config').select('*').eq('id', 1).single(),
    chargerPrimes(),
    chargerAjustements(),
  ])
  const c = (cfg.data ?? {}) as Record<string, unknown>
  /* `kind` absent (migration 0062 pas en base) = commande, comme avant. */
  const estPremium = (p: Record<string, unknown>) => p.kind === 'premium'

  return {
    abonnementsPremium: paiements.filter(estPremium).map((p) => ({
      id: String(p.id),
      clientId: String(p.client_id),
      operateur: String(p.provider ?? ''),
      montant: Number(p.amount ?? 0),
      statut: p.status === 'refunded' ? ('refunded' as const) : ('succeeded' as const),
      recuLe: jour((p.confirmed_at as string | null) ?? (p.created_at as string)),
    })),
    paiements: paiements.filter((p) => !estPremium(p)).map((p) => ({
      id: String(p.id),
      commandeId: String(p.order_id),
      operateur: String(p.provider ?? ''),
      montant: Number(p.amount ?? 0),
      statut: p.status === 'refunded' ? 'refunded' : 'succeeded',
      recuLe: jour((p.confirmed_at as string | null) ?? (p.created_at as string)),
      rembourseLe: p.refunded_at ? jour(p.refunded_at as string) : null,
    })),
    paiesLivreurs: paies.map((v) => ({
      id: String(v.id),
      beneficiaire: String(v.driver_id),
      operateur: (v.provider as string | null) ?? null,
      montant: Number(v.total ?? 0),
      verseLe: jour((v.paid_at as string | null) ?? (v.period_end as string)),
    })),
    primesLivreurs: primes,
    reversementsMarchands: reversements.map((v) => ({
      id: String(v.id),
      beneficiaire: String(v.merchant_id),
      operateur: (v.payout_provider as string | null) ?? null,
      montant: Number(v.net_payable ?? 0),
      verseLe: jour((v.paid_at as string | null) ?? (v.period_end as string)),
      fraisRetenus: Number(v.frais_marchand ?? 0),
    })),
    ajustementsMarchands: ajustements,
    taux: {
      encaissement: { wave: taux(c.frais_mm_encaissement_wave), orange_money: taux(c.frais_mm_encaissement_om) },
      decaissement: { wave: taux(c.frais_mm_decaissement_wave), orange_money: taux(c.frais_mm_decaissement_om) },
    },
  }
}

/**
 * Retenues et crédits marchands (0065), à la date de Bamako de leur enregistrement.
 * ⚠️ Table absente (migration pas encore poussée) : liste VIDE, pas une panne.
 */
async function chargerAjustements(): Promise<AjustementMarchand[]> {
  try {
    const lignes = await toutesLesLignes<Record<string, unknown>>((de, a) =>
      supabase.from('merchant_adjustments').select('id,merchant_id,montant,motif,created_at').order('created_at').order('id').range(de, a),
    )
    return lignes.map((l) => ({
      id: String(l.id),
      beneficiaire: String(l.merchant_id),
      montant: Number(l.montant ?? 0),
      date: jour(l.created_at as string),
      motif: String(l.motif ?? ''),
    }))
  } catch {
    return []
  }
}

const GENRES_PRIME = new Set(['pointe', 'volume', 'fidelite'])

/**
 * Toutes les primes acquises jusqu'à aujourd'hui (date de Bamako = UTC).
 *
 * ⚠️ RPC absente (migration 0060 pas encore poussée) : liste VIDE, pas une
 * panne — il n'y avait alors aucune prime, ni dans la paie ni ici.
 */
async function chargerPrimes(): Promise<PrimeAcquise[]> {
  const aujourdhui = new Date().toISOString().slice(0, 10)
  try {
    const lignes = await toutesLesLignes<Record<string, unknown>>((de, a) =>
      supabase
        .rpc('primes_livreurs', { p_start: '2000-01-01', p_end: aujourdhui })
        .order('jour')
        .order('driver_id')
        .order('genre')
        .range(de, a),
    )
    return primesDepuisLignes(lignes)
  } catch {
    return []
  }
}

/** Lignes brutes de `primes_livreurs` → primes typées ; ignore ce qui n'est pas exploitable. */
export function primesDepuisLignes(lignes: Array<Record<string, unknown>>): PrimeAcquise[] {
  return lignes
    .filter((l) => GENRES_PRIME.has(String(l.genre)) && Number(l.montant) > 0 && l.jour)
    .map((l) => ({
      livreurId: String(l.driver_id),
      genre: String(l.genre) as PrimeAcquise['genre'],
      jour: String(l.jour).slice(0, 10),
      montant: Number(l.montant),
      courses: Number(l.courses ?? 0),
    }))
}

const LIBELLE_PRIME: Record<PrimeAcquise['genre'], string> = {
  pointe: 'Prime pointe',
  volume: 'Prime volume',
  fidelite: 'Prime fidélité',
}

/** Frais d'opérateur sur un mouvement, ou `null` si le taux n'est pas connu. */
export function fraisOperateur(montant: number, operateur: string | null, t: Record<Operateur, number | null>): number | null {
  const r = operateur === 'wave' || operateur === 'orange_money' ? t[operateur] : null
  return r === null ? null : Math.round(montant * r)
}

/**
 * Tous les événements comptables de la période, à partir de ce qui s'est
 * RÉELLEMENT passé.
 *
 * Le compte 4191 sert de pivot : le client paie (551 / 4191), puis la
 * livraison répartit son argent entre marchand, livreur, État et nous
 * (4191 / …). Ce qui n'est pas encore livré reste au 4191 — une avance.
 * Chaque sortie d'argent (paie, reversement, remboursement) n'est passée que
 * quand elle a EU LIEU, à sa date.
 *
 * Un type absent du plan ou non automatique n'est simplement pas généré :
 * tant que la migration 0044 n'est pas poussée, les nouveaux types n'existent
 * pas et le journal garde l'ancien câblage, sans planter.
 */
export function evenements(
  commandes: Commande[],
  plan: PlanComptable,
  tva: ParametresTVA = TVA_INACTIVE,
  mvt: Mouvements = MOUVEMENTS_VIDES,
): EvenementComptable[] {
  const parCode = new Map(plan.types.map((t) => [t.code, t]))
  const auto = (c: string) => parCode.get(c)?.auto ?? false
  const ev: EvenementComptable[] = []
  const pousser = (e: EvenementComptable) => {
    if (auto(e.tx) && e.montant > 0 && e.date) ev.push(e)
  }
  const parCommande = new Map(commandes.map((c) => [c.id, c]))

  // ── À la livraison : l'argent du client est réparti ────────────────
  for (const o of commandes.filter((c) => c.statut === 'delivered')) {
    const d = decomposer(o, tva)
    const sur = (tx: string, montant: number, libelle: string) =>
      pousser({ date: d.date, tx, montant, libelle, source: 'order' })
    sur('MERCHANT_DUE', d.dûAuMarchand, `Part marchand ${d.ref}`)
    /* Les produits entrent HORS TAXES, et la taxe suit dans son écriture à
       elle. TVA désactivée, `…HT` vaut le TTC et `tvaCollectee` vaut zéro. */
    sur('COMMISSION', d.commissionHT, `Commission ${d.ref}`)
    sur('DELIVERY_FEE', d.livraisonHT, `Livraison ${d.ref}`)
    sur('SERVICE_FEE', d.accessoiresHT, `Frais service ${d.ref}`)
    sur('VAT_COLLECTED', d.tvaCollectee, `TVA facturée ${d.ref}`)
    sur('TIP_DUE', d.pourboire, `Pourboire ${d.ref}`)
    sur('DRIVER_PAYMENT', d.partLivreur, `Part livreur ${d.ref}`)
  }

  // ── Primes livreurs : une charge due, le jour où elle est acquise ──
  /* Pointe : jour de la course ; volume : jour du seuil ; fidélité : dernier
     jour du mois. 637 / 4013 — la paie versée (4013 / 551) les inclut. */
  for (const p of mvt.primesLivreurs) {
    const detail = p.genre === 'fidelite' ? `${p.courses} courses dans le mois` : `${p.courses} course${p.courses > 1 ? 's' : ''}`
    pousser({ date: p.jour, tx: 'DRIVER_BONUS', montant: p.montant, libelle: `${LIBELLE_PRIME[p.genre]} ${p.jour} (${detail})`, source: 'driver_bonus' })
  }

  // ── Encaissements, remboursements et frais retenus ─────────────────
  for (const p of mvt.paiements) {
    const o = parCommande.get(p.commandeId)
    const ref = o?.ref ?? p.commandeId.slice(0, 8)
    pousser({ date: p.recuLe, tx: 'PAYMENT_RECEIVED', montant: p.montant, libelle: `Encaissement ${ref}`, source: 'payment' })
    const fEnc = fraisOperateur(p.montant, p.operateur, mvt.taux.encaissement)
    if (fEnc) pousser({ date: p.recuLe, tx: 'MM_FEE_AUTO', montant: fEnc, libelle: `Frais encaissement ${ref}`, source: 'fee' })

    if (p.statut === 'refunded') {
      /* Le montant remboursé est celui fixé à l'annulation (frais de service
         gardés si le client annule lui-même). À défaut, tout le paiement. */
      const rembourse = Math.min(p.montant, o?.montantRembourse ?? p.montant)
      const date = p.rembourseLe ?? (jour(o?.annuleeLe) || p.recuLe)
      pousser({ date, tx: 'REFUND', montant: rembourse, libelle: `Remboursement ${ref}`, source: 'refund' })
      const fDec = fraisOperateur(rembourse, p.operateur, mvt.taux.decaissement)
      if (fDec) pousser({ date, tx: 'MM_FEE_AUTO', montant: fDec, libelle: `Frais remboursement ${ref}`, source: 'fee' })
    }

    /* Commande payée puis ANNULÉE : ce qui n'est pas rendu au client (les
       frais de service quand il annule lui-même) devient un produit, à la
       date d'annulation. Sans cette écriture il resterait indéfiniment au
       4191, comme si on le lui devait encore. */
    if (o?.statut === 'cancelled' && o.montantRembourse !== null && o.montantRembourse !== undefined) {
      const retenu = Math.max(0, p.montant - o.montantRembourse)
      if (retenu > 0) {
        const t = extraireTVA(retenu, tva)
        const date = jour(o.annuleeLe) || p.recuLe
        pousser({ date, tx: 'SERVICE_FEE', montant: t.ht, libelle: `Frais retenus (annulation) ${ref}`, source: 'order' })
        pousser({ date, tx: 'VAT_COLLECTED', montant: t.tva, libelle: `TVA frais retenus ${ref}`, source: 'order' })
      }
    }
  }

  // ── Abonnements Premium : produit encaissé directement (0062) ──────
  /* 551 / 7063 hors taxes, 551 / 443 pour la TVA EXTRAITE du TTC (0039), à la date de
     l'encaissement. Pas de produit constaté d'avance : SwiftEats n'en passe pour aucun
     produit et la période couverte ne dépasse jamais un mois (docs/PREMIUM.md). Un
     abonnement remboursé n'est PAS extourné automatiquement : alerte, saisie manuelle. */
  for (const p of mvt.abonnementsPremium) {
    const ref = `Premium ${p.id.slice(0, 8)}`
    const t = extraireTVA(p.montant, tva)
    pousser({ date: p.recuLe, tx: 'PREMIUM_SUB', montant: t.ht, libelle: `Abonnement ${ref}`, source: 'premium' })
    pousser({ date: p.recuLe, tx: 'PREMIUM_VAT', montant: t.tva, libelle: `TVA abonnement ${ref}`, source: 'premium' })
    const f = fraisOperateur(p.montant, p.operateur, mvt.taux.encaissement)
    if (f) pousser({ date: p.recuLe, tx: 'MM_FEE_AUTO', montant: f, libelle: `Frais encaissement ${ref}`, source: 'fee' })
  }

  // ── Sorties d'argent réellement faites ─────────────────────────────
  for (const v of mvt.paiesLivreurs) {
    pousser({ date: v.verseLe, tx: 'DRIVER_PAYOUT', montant: v.montant, libelle: `Paie livreur ${v.verseLe}`, source: 'driver_payout' })
    const f = fraisOperateur(v.montant, v.operateur, mvt.taux.decaissement)
    if (f) pousser({ date: v.verseLe, tx: 'MM_FEE_AUTO', montant: f, libelle: `Frais envoi paie ${v.verseLe}`, source: 'fee' })
  }
  for (const v of mvt.reversementsMarchands) {
    const nom = commandes.find((c) => c.marchandId === v.beneficiaire)?.marchandNom ?? 'marchand'
    pousser({ date: v.verseLe, tx: 'RESTAURANT_PAYOUT', montant: v.montant, libelle: `Reversement ${nom} ${v.verseLe}`, source: 'merchant_payout' })
    /* Frais de virement retenus au marchand (futur : décision actuelle = inclus dans notre commission, donc 0). */
    if (v.fraisRetenus) pousser({ date: v.verseLe, tx: 'MERCHANT_FEE_RETAINED', montant: v.fraisRetenus, libelle: `Frais de virement retenus ${nom} ${v.verseLe}`, source: 'merchant_payout' })
    const f = fraisOperateur(v.montant, v.operateur, mvt.taux.decaissement)
    if (f) pousser({ date: v.verseLe, tx: 'MM_FEE_AUTO', montant: f, libelle: `Frais envoi reversement ${v.verseLe}`, source: 'fee' })
  }

  /* Retenues et crédits après reversement (0065) : 401 / 4191 et 4191 / 401, à la date où ils sont décidés. Ce sont des
     mouvements de DETTE : la commission et les frais de la commande remboursée, eux, ne sont pas extournés ici (alerte). */
  for (const a of mvt.ajustementsMarchands ?? []) {
    const nom = commandes.find((c) => c.marchandId === a.beneficiaire)?.marchandNom ?? 'marchand'
    if (a.montant < 0) pousser({ date: a.date, tx: 'MERCHANT_DEDUCTION', montant: -a.montant, libelle: `Retenue ${nom} : ${a.motif}`, source: 'merchant_adjustment' })
    else if (a.montant > 0) pousser({ date: a.date, tx: 'MERCHANT_CREDIT', montant: a.montant, libelle: `Crédit ${nom} : ${a.motif}`, source: 'merchant_adjustment' })
  }

  for (const m of plan.manuelles) {
    ev.push({ date: m.date, tx: m.tx, montant: m.amount, libelle: m.label, source: 'manual' })
  }
  return ev
}

/** Un point qui empêche de prendre les chiffres pour argent comptant. */
export interface AlerteCompta {
  niveau: 'erreur' | 'attention'
  message: string
}

/**
 * Ce que la comptabilité ne peut pas encore affirmer, dit à l'écran plutôt
 * que masqué derrière des totaux d'apparence normale.
 */
export function controlesCompta(commandes: Commande[], plan: PlanComptable, mvt: Mouvements): AlerteCompta[] {
  const alertes: AlerteCompta[] = []
  if (!plan.types.some((t) => t.code === 'PAYMENT_RECEIVED')) {
    alertes.push({
      niveau: 'erreur',
      message:
        'La migration 0044 n’est pas encore appliquée : le journal suit l’ancien schéma (livreur réputé payé à la livraison, encaissements et pourboires absents).',
    })
  }

  const nomOp: Record<Operateur, string> = { wave: 'Wave', orange_money: 'Orange Money' }
  const manquants = new Set<string>()
  for (const p of [...mvt.paiements, ...mvt.abonnementsPremium]) {
    const op = p.operateur as Operateur
    if (op in nomOp && mvt.taux.encaissement[op] === null) manquants.add(`encaissement ${nomOp[op]}`)
    if (p.statut === 'refunded' && op in nomOp && mvt.taux.decaissement[op] === null) manquants.add(`envoi ${nomOp[op]}`)
  }
  for (const v of [...mvt.paiesLivreurs, ...mvt.reversementsMarchands]) {
    const op = v.operateur as Operateur
    if (op in nomOp && mvt.taux.decaissement[op] === null) manquants.add(`envoi ${nomOp[op]}`)
  }
  if (manquants.size > 0) {
    alertes.push({
      niveau: 'attention',
      message: `Taux de frais non renseigné (${[...manquants].join(', ')}) : ces frais ne sont pas comptés, la trésorerie affichée est donc trop haute. À saisir dans Config dès que les contrats sont signés.`,
    })
  }

  /* Des primes existent (0060) mais leur règle d'écriture manque ou est
     désactivée : la paie versée les inclut, le 4013 deviendrait débiteur. */
  const regleBonus = plan.types.find((t) => t.code === 'DRIVER_BONUS')
  if (mvt.primesLivreurs.length > 0 && !regleBonus?.auto) {
    const total = mvt.primesLivreurs.reduce((s, p) => s + p.montant, 0)
    alertes.push({
      niveau: 'erreur',
      message: `Primes livreurs (${total.toLocaleString('fr-FR')} F) non passées au journal : le type d’écriture DRIVER_BONUS (637 / 4013) est absent ou non automatique. La paie versée les inclut, la dette envers les livreurs est donc fausse.`,
    })
  }

  /* Abonnements Premium encaissés mais règle d'écriture absente ou manuelle : l'argent
     serait au 551 sans contrepartie — le bilan ne tomberait plus juste. */
  if (mvt.abonnementsPremium.length > 0) {
    const regle = plan.types.find((t) => t.code === 'PREMIUM_SUB')
    const regleTva = plan.types.find((t) => t.code === 'PREMIUM_VAT')
    const total = mvt.abonnementsPremium.reduce((s, p) => s + p.montant, 0)
    if (!regle?.auto || (tvaActive(plan) && !regleTva?.auto)) {
      alertes.push({
        niveau: 'erreur',
        message: `Abonnements Premium encaissés (${total.toLocaleString('fr-FR')} F) non passés au journal : les types PREMIUM_SUB (551 / 7063) et PREMIUM_VAT (551 / 443) doivent exister et être automatiques (migration 0062).`,
      })
    }
    const rembourses = mvt.abonnementsPremium.filter((p) => p.statut === 'refunded')
    if (rembourses.length > 0) {
      alertes.push({
        niveau: 'attention',
        message: `${rembourses.length} abonnement(s) Premium marqué(s) remboursé(s) (${rembourses.reduce((s, p) => s + p.montant, 0).toLocaleString('fr-FR')} F) : le remboursement n'est pas passé automatiquement. À saisir en écriture manuelle (extourne du produit 7063 et sortie du 551).`,
      })
    }
  }

  /* Retenues / crédits marchands présents mais règles d'écriture absentes : la dette 401 serait fausse. */
  const aj = mvt.ajustementsMarchands ?? []
  if (aj.length > 0) {
    const regleRet = plan.types.find((t) => t.code === 'MERCHANT_DEDUCTION')
    const regleCred = plan.types.find((t) => t.code === 'MERCHANT_CREDIT')
    if ((aj.some((a) => a.montant < 0) && !regleRet?.auto) || (aj.some((a) => a.montant > 0) && !regleCred?.auto)) {
      alertes.push({
        niveau: 'erreur',
        message: 'Des retenues ou crédits marchands existent (0065) mais les types MERCHANT_DEDUCTION (401 / 4191) et MERCHANT_CREDIT (4191 / 401) sont absents ou non automatiques : la dette envers les marchands est fausse.',
      })
    }
    const livreesRemboursees = commandes.filter((c) => c.statut === 'delivered' && mvt.paiements.some((p) => p.commandeId === c.id && p.statut === 'refunded'))
    if (livreesRemboursees.length > 0) {
      alertes.push({
        niveau: 'attention',
        message: `${livreesRemboursees.length} commande(s) livrée(s) puis remboursée(s) : la part du marchand est retenue sur son reversement, mais notre commission, la livraison et les frais ne sont pas extournés automatiquement. À régulariser en écriture manuelle (le solde correspondant reste au 4191).`,
      })
    }
  }

  const payees = new Set(mvt.paiements.map((p) => p.commandeId))
  const sansPaiement = commandes.filter((c) => c.statut === 'delivered' && !payees.has(c.id))
  if (sansPaiement.length > 0) {
    const total = sansPaiement.reduce((s, c) => s + (c.total || 0), 0)
    alertes.push({
      niveau: 'attention',
      message: `Anomalie : ${sansPaiement.length} commande(s) livrée(s) sans paiement enregistré (${total.toLocaleString('fr-FR')} F). Aucune commande ne devrait être livrée sans paiement préalable : il s’agit de commandes de test, ou d’un paiement reçu mais absent de la base. Tant que ce n’est pas réglé, ces montants restent au 4191.`,
    })
  }
  return alertes
}

const tvaActive = (plan: PlanComptable) => Boolean(plan.tva?.actif)

export interface LigneJournal {
  date: string
  tx: string
  compte: string
  sens: 'debit' | 'credit'
  montant: number
  libelle: string
}

/** Journal : deux lignes par événement — un débit, un crédit, du même montant. */
export function journal(ev: EvenementComptable[], plan: PlanComptable): LigneJournal[] {
  const parCode = new Map(plan.types.map((t) => [t.code, t]))
  const lignes: LigneJournal[] = []
  for (const e of ev) {
    const t = parCode.get(e.tx)
    if (!t) continue
    lignes.push({ date: e.date, tx: e.tx, compte: t.debit, sens: 'debit', montant: e.montant, libelle: e.libelle })
    lignes.push({ date: e.date, tx: e.tx, compte: t.credit, sens: 'credit', montant: e.montant, libelle: e.libelle })
  }
  return lignes.sort((a, b) => b.date.localeCompare(a.date))
}

export interface CompteGrandLivre {
  code: string
  label: string
  classe: number
  debit: number
  credit: number
  solde: number
}

export type GrandLivre = Record<string, CompteGrandLivre>

export function grandLivre(lignes: LigneJournal[], plan: PlanComptable): GrandLivre {
  const gl: GrandLivre = {}
  for (const a of plan.comptes) {
    gl[a.code] = { code: a.code, label: a.label, classe: a.class, debit: 0, credit: 0, solde: 0 }
  }
  for (const l of lignes) {
    /* Un compte mouvementé mais ABSENT du plan est créé à la volée. L'ignorer
       ferait disparaître son montant du grand-livre — et le bilan serait
       déséquilibré d'une somme sans trace. */
    const c = (gl[l.compte] ??= {
      code: l.compte,
      label: l.compte,
      classe: 0,
      debit: 0,
      credit: 0,
      solde: 0,
    })
    if (l.sens === 'debit') c.debit += l.montant
    else c.credit += l.montant
  }
  for (const c of Object.values(gl)) c.solde = c.debit - c.credit
  return gl
}

// ── Indicateurs ──────────────────────────────────────────────────────────

export interface Indicateurs {
  gmv: number
  ca: number
  charges: number
  resultat: number
  nbCommandes: number
  detteMarchands: number
  /** Pourboires et rémunérations dus aux livreurs, pas encore versés. */
  detteLivreurs: number
  /** Payé par des clients, pas encore livré (ou remboursement encore dû). */
  avancesClients: number
  /** Livré, jamais encaissé. */
  creancesClients: number
  floatMobileMoney: number
}

export function indicateurs(
  commandes: Commande[],
  ev: EvenementComptable[],
  gl: GrandLivre,
  plan: PlanComptable,
): Indicateurs {
  const parCode = new Map(plan.types.map((t) => [t.code, t]))
  const somme = (genre: string) =>
    ev.filter((e) => parCode.get(e.tx)?.kind === genre).reduce((s, e) => s + e.montant, 0)

  const livrees = commandes.filter((c) => c.statut === 'delivered')
  const ca = somme('revenue')
  const charges = somme('expense')
  const c401 = gl['401']
  const c551 = gl['551']
  /* Les comptes de pivot sont lus dans les RÈGLES (transaction_types), pas
     écrits en dur : si l'expert-comptable fait changer 4013 ou 4191, les
     indicateurs suivent. */
  const solde = (code: string | undefined) => (code ? gl[code]?.solde ?? 0 : 0)
  const cLivreurs = parCode.get('DRIVER_PAYMENT')?.credit
  const cClients = parCode.get('PAYMENT_RECEIVED')?.credit

  return {
    gmv: livrees.reduce((s, o) => s + o.total, 0),
    ca,
    charges,
    resultat: ca - charges,
    nbCommandes: livrees.length,
    // 401 est un compte de PASSIF : son solde créditeur est ce qu'on doit.
    detteMarchands: c401 ? Math.max(0, c401.credit - c401.debit) : 0,
    detteLivreurs: cLivreurs && cLivreurs !== '551' ? Math.max(0, -solde(cLivreurs)) : 0,
    avancesClients: cClients ? Math.max(0, -solde(cClients)) : 0,
    creancesClients: cClients ? Math.max(0, solde(cClients)) : 0,
    floatMobileMoney: c551 ? c551.solde : 0,
  }
}

export interface MoisComptable {
  mois: string
  gmv: number
  ca: number
  charges: number
}

export function parMois(
  commandes: Commande[],
  ev: EvenementComptable[],
  plan: PlanComptable,
): MoisComptable[] {
  const parCode = new Map(plan.types.map((t) => [t.code, t]))
  const m: Record<string, MoisComptable> = {}
  const entree = (cle: string) => (m[cle] ??= { mois: cle, gmv: 0, ca: 0, charges: 0 })

  for (const o of commandes.filter((c) => c.statut === 'delivered')) {
    const cle = (o.horodatages.delivered ?? o.creeeLe ?? '').slice(0, 7)
    if (cle) entree(cle).gmv += o.total
  }
  for (const e of ev) {
    const cle = e.date.slice(0, 7)
    if (!cle) continue
    const genre = parCode.get(e.tx)?.kind
    if (genre === 'revenue') entree(cle).ca += e.montant
    if (genre === 'expense') entree(cle).charges += e.montant
  }
  return Object.keys(m)
    .sort()
    .map((k) => m[k] as MoisComptable)
}

export interface RegimeFiscal {
  regime: string
  tva: string
  precision: string
  couleur: string
}

/**
 * Régime fiscal malien (DGI / CGI).
 *
 * ⚠️ Le seuil se lit sur le CA RÉEL — commissions et frais — et jamais sur le
 * GMV. Dans un modèle agent l'écart est d'un ordre de grandeur : confondre les
 * deux ferait basculer l'entreprise au réel normal et déclarer une TVA de 18 %
 * sur un chiffre d'affaires qu'elle ne réalise pas.
 */
export function regimeFiscal(caAnnuel: number): RegimeFiscal {
  if (caAnnuel < 50e6) {
    return {
      regime: 'Impôt synthétique',
      tva: 'Non assujetti',
      precision: 'CA < 50 M — IS ~3 % du CA, pas de TVA',
      couleur: '--green',
    }
  }
  if (caAnnuel < 100e6) {
    return {
      regime: 'Réel simplifié',
      tva: 'Selon option DGI',
      precision: 'CA 50–100 M — TVA selon option / DGI',
      couleur: '--warning',
    }
  }
  return {
    regime: 'Réel normal',
    tva: 'TVA 18 %',
    precision: 'CA > 100 M — TVA 18 % applicable',
    couleur: '--accent',
  }
}

/** CA annualisé à partir des mois observés. Projection, pas une constatation. */
export function caAnnualise(mois: MoisComptable[]): number {
  if (mois.length === 0) return 0
  const total = mois.reduce((s, m) => s + m.ca, 0)
  return Math.round((total / mois.length) * 12)
}

// ── États OHADA ──────────────────────────────────────────────────────────

/**
 * Somme des comptes dont le code commence par l'un des préfixes, du bon côté.
 *
 * Le côté compte : un poste d'ACTIF ne retient que les soldes débiteurs, un
 * poste de PASSIF que les créditeurs. Sans ce filtre, un compte de trésorerie
 * à découvert (donc créditeur) s'ajouterait à l'actif en négatif au lieu
 * d'alimenter le passif — et le bilan ne s'équilibrerait plus.
 */
export function sommeSection(gl: GrandLivre, prefixes: string[], sens: 'D' | 'C'): number {
  return Object.values(gl)
    .filter((a) => prefixes.some((p) => a.code.startsWith(p)))
    .reduce((s, a) => {
      if (sens === 'D' && a.solde > 0) return s + a.solde
      if (sens === 'C' && a.solde < 0) return s - a.solde
      return s
    }, 0)
}

export function comptesDeSection(
  gl: GrandLivre,
  prefixes: string[],
  sens: 'D' | 'C',
): Array<{ code: string; label: string; montant: number }> {
  return Object.values(gl)
    .filter((a) => prefixes.some((p) => a.code.startsWith(p)))
    .map((a) => ({ code: a.code, label: a.label, montant: sens === 'D' ? a.solde : -a.solde }))
    .filter((a) => a.montant > 0)
    .sort((a, b) => b.montant - a.montant)
}

/**
 * Évalue les postes d'un état : d'abord les feuilles (agrégation de comptes),
 * puis les formules, en plusieurs passes car une formule peut dépendre d'une
 * autre. Le garde-fou à 30 passes empêche une boucle infinie si un mapping
 * introduit une dépendance circulaire.
 */
export function valeursOhada(
  postes: PosteOhada[],
  gl: GrandLivre,
  prefixes: Record<string, string[]>,
  resultatNet: number,
): Record<string, number> {
  const v: Record<string, number> = {}

  for (const r of postes) {
    if (r.special === 'res') {
      v[r.ref] = resultatNet
      continue
    }
    if (r.f) continue
    v[r.ref] = r.manual ? 0 : sommeSection(gl, prefixes[r.ref] ?? [], r.side ?? 'D')
  }

  let restants = postes.filter((r) => r.f)
  let garde = 0
  while (restants.length > 0 && garde++ < 30) {
    const avant = restants.length
    restants = restants.filter((r) => {
      const f = r.f
      if (!f) return false
      if (!f.every(([, ref]) => v[ref] !== undefined)) return true
      v[r.ref] = f.reduce((s, [op, ref]) => s + (op === '-' ? -1 : 1) * (v[ref] ?? 0), 0)
      return false
    })
    if (restants.length === avant) break
  }
  return v
}

/** Le mapping actif, avec les ajustements éventuels de l'utilisateur. */
export function prefixesActifs(): Record<string, string[]> {
  try {
    const brut = localStorage.getItem('se_ohada_pref_v1')
    if (brut) return { ...PREFIXES_PAR_DEFAUT, ...(JSON.parse(brut) as Record<string, string[]>) }
  } catch {
    /* Un stockage illisible ne doit pas empêcher la comptabilité de s'afficher. */
  }
  return PREFIXES_PAR_DEFAUT
}

/**
 * Enregistre une écriture saisie à la main.
 *
 * ⚠️ DEUX noms de colonnes à ne pas inventer, et je m'étais trompé sur le
 * second :
 *   • `entry_date` et non `date` — `date` est un mot réservé SQL ;
 *   • `tx_type` et non `tx_code` — la LECTURE juste au-dessus utilise bien
 *     `tx_type`, mais l'écriture avait été écrite de mémoire. Chaque saisie
 *     aurait été refusée par Postgres, sur le seul écran qui permet de faire
 *     entrer une charge hors commandes dans le résultat.
 *
 * Seuls les types marqués MANUELS doivent arriver ici : un type « auto » est
 * déjà généré depuis les commandes, et le saisir à la main le compterait deux
 * fois dans le résultat.
 */
export async function ajouterEcritureManuelle(e: {
  date: string
  tx: string
  montant: number
  libelle: string
}): Promise<EcritureManuelle> {
  const { data, error } = await supabase
    .from('manual_entries')
    .insert({ entry_date: e.date, tx_type: e.tx, amount: e.montant, label: e.libelle })
    .select('*')
    .single<{ id: string; entry_date: string; tx_type: string; amount: number; label: string }>()
  if (error) throw error
  // Même troncature que la lecture : sinon la ligne fraîche se trie autrement
  // que les autres tant qu'on n'a pas rechargé.
  return {
    id: data.id,
    date: String(data.entry_date).slice(0, 10),
    tx: data.tx_type,
    amount: data.amount,
    label: data.label,
  }
}

// ── Décomposition par ligne (fiche commande du dispatch, 0055) ───────────

export interface LigneDecomposee {
  nom: string
  quantite: number
  /** Total payé par le client pour la ligne. */
  prixClient: number
  /** Ce qui revient au marchand. */
  prixMarchand: number
  commission: number
  /** Taux figé, en fraction. */
  taux: number
  /** true = commission ajoutée au prix (0055) ; false = commission retenue (avant 0055). */
  ajoutee: boolean
}

/** Prix client / prix marchand / commission de chaque ligne, dans les deux modèles. */
export function decomposerLignes(o: Pick<Commande, 'articles'>): LigneDecomposee[] {
  return (o.articles ?? []).map((l) => {
    const prixClient = l.total ?? l.unit_price * l.quantity
    const ajoutee = l.montant_marchand != null && l.montant_commission != null
    const commission = ajoutee ? l.montant_commission! : Math.round(prixClient * (l.commission_pct ?? 0))
    return {
      nom: l.name,
      quantite: l.quantity,
      prixClient,
      prixMarchand: ajoutee ? l.montant_marchand! : prixClient - commission,
      commission,
      taux: l.commission_pct ?? 0,
      ajoutee,
    }
  })
}
